import { __spreadArray } from "tslib";
import { isSimpleStream, isLoadableStream, } from './types';
import { selectors as streamSelectors } from '../../Stream';
import { reduxKey, initialState } from './constants';
import { getStreamItemId, isDeviation } from './helpers';
import { getIsMobile, getPagingMode } from '../../publicSession/selectors';
import { getCurrentModal } from '../../Modals/redux/selectors';
import { isAnyEditorFocused } from '../../DaEditorWrapper/redux/selectors';
import { getExtendedDeviationById, getDeviationById, } from '../../redux/deviations/selectors';
function getDuperbrowseState(state) {
    return state[reduxKey] || initialState;
}
function getTopStream(state) {
    var _a = getDuperbrowseState(state), streamStack = _a.streamStack, rootStream = _a.rootStream;
    if (!streamStack.length) {
        return rootStream;
    }
    return streamStack[0];
}
function getExtendedItem(state, item, itemSchema) {
    var _a;
    if (item === undefined) {
        return;
    }
    switch (itemSchema) {
        case 'deviation':
            if (isDeviation(item)) {
                return (_a = getExtendedDeviationById(state, item.deviationId)) !== null && _a !== void 0 ? _a : item;
            }
            if (typeof item === 'number' || typeof item === 'string') {
                return getExtendedDeviationById(state, item);
            }
            return;
        default:
            return item;
    }
}
function getSimpleItem(state, item, itemSchema) {
    if (item === undefined) {
        return;
    }
    switch (itemSchema) {
        case 'deviation':
            if (isDeviation(item)) {
                return item;
            }
            if (typeof item === 'number' || typeof item === 'string') {
                return getDeviationById(state, item);
            }
            return;
        default:
            return item;
    }
}
export function getStreamItems(state, stream) {
    if (isSimpleStream(stream)) {
        return stream.items;
    }
    if (stream.streamItemSelector) {
        return stream.streamItemSelector(state, stream.streamId);
    }
    var streamItems = streamSelectors.getItems(state, stream.streamId);
    return streamItems;
}
export function getCurrentItem(state) {
    var topStream = getTopStream(state);
    if (!topStream) {
        return;
    }
    var items = getStreamItems(state, topStream);
    var item = items.find(function (i) { return getStreamItemId(i) === topStream.currentOpenItem; });
    return getExtendedItem(state, item, topStream.itemSchema);
}
function getCurrentItemIndex(state) {
    var currentStream = getTopStream(state);
    if (!currentStream) {
        return -1;
    }
    var items = getStreamItems(state, currentStream);
    return items.map(getStreamItemId).indexOf(currentStream.currentOpenItem);
}
function getStreamItemAtIndex(state, index) {
    var currentStream = getTopStream(state);
    if (!currentStream || index < 0) {
        return;
    }
    var items = getStreamItems(state, currentStream);
    return items[index];
}
function getNextItemNormalized(state) {
    var currentStream = getTopStream(state);
    if (!currentStream) {
        return;
    }
    var index = getCurrentItemIndex(state);
    if (index < 0) {
        return;
    }
    return getStreamItemAtIndex(state, index + 1);
}
export function getNextItem(state) {
    var currentStream = getTopStream(state);
    if (!currentStream) {
        return;
    }
    return getExtendedItem(state, getNextItemNormalized(state), currentStream.itemSchema);
}
function getPrevItemNormalized(state) {
    var currentStream = getTopStream(state);
    if (!currentStream) {
        return;
    }
    var index = getCurrentItemIndex(state);
    if (index < 0) {
        return;
    }
    return getStreamItemAtIndex(state, index - 1);
}
export function getPrevItem(state) {
    var currentStream = getTopStream(state);
    if (!currentStream) {
        return;
    }
    return getExtendedItem(state, getPrevItemNormalized(state), currentStream.itemSchema);
}
export function getIsDuperbrowseActive(state) {
    return getDuperbrowseState(state).isSessionActive;
}
export function getItemIdsForPreload(state, preloadDirection) {
    var nextItem = getNextItemNormalized(state);
    var prevItem = getPrevItemNormalized(state);
    var shouldPreloadDirection = function (direction) {
        return !preloadDirection || preloadDirection === direction;
    };
    var navigation = [
        nextItem && shouldPreloadDirection('next') && getStreamItemId(nextItem),
        prevItem && shouldPreloadDirection('prev') && getStreamItemId(prevItem),
    ].filter(function (item) { return !!item; });
    return navigation;
}
export function getDuperbrowseNavigation(state) {
    return {
        nextItem: getNextItem(state),
        prevItem: getPrevItem(state),
    };
}
export function getDuperbrowseStreamParent(state) {
    var currentStream = getDuperbrowseState(state).streamStack[0];
    return currentStream && currentStream.parent;
}
export function getIsKeyboardDuperbrowseAllowed(state) {
    // say no to mobile
    if (getIsMobile(state)) {
        return false;
    }
    // modals get in the way, too
    if (getCurrentModal(state)) {
        return false;
    }
    if (isAnyEditorFocused(state)) {
        return false;
    }
    return true;
}
export function getIsLoading(state) {
    var currentStreamId = getCurrentStreamId(state);
    if (currentStreamId &&
        streamSelectors.getIsValidStream(state, currentStreamId)) {
        return streamSelectors.isFetching(state, currentStreamId);
    }
    return false;
}
export function getLastViewedItem(state) {
    return getDuperbrowseState(state).lastViewedItem;
}
export function getIsValidStreamItem(state, streamId, itemId) {
    var _a = getDuperbrowseState(state), streamStack = _a.streamStack, rootStream = _a.rootStream;
    return __spreadArray(__spreadArray([], streamStack, true), [rootStream], false).some(function (stream) {
        return !!stream &&
            stream.streamId === streamId &&
            getStreamItems(state, stream).some(function (item) { return getStreamItemId(item) === itemId; });
    });
}
export function getCurrentStreamId(state) {
    var _a = getDuperbrowseState(state), topStream = _a.streamStack[0], rootStream = _a.rootStream;
    if (topStream) {
        return topStream.streamId;
    }
    if (rootStream) {
        return rootStream.streamId;
    }
}
export function getRootLocation(state) {
    return getDuperbrowseState(state).rootLocation;
}
export function getCurrentStreamSchema(state) {
    var currentStream = getTopStream(state);
    return currentStream && currentStream.itemSchema;
}
export function getIsAnyStreamActive(state) {
    var duperState = getDuperbrowseState(state);
    return duperState.streamStack.length > 0 || !!duperState.rootStream;
}
export function getIsHistoryManagementEnabled(state) {
    var _a, _b;
    return (_b = (_a = getDuperbrowseState(state)) === null || _a === void 0 ? void 0 : _a.isHistoryManagementEnabled) !== null && _b !== void 0 ? _b : true;
}
export function getNeedsToLoadMore(state, direction) {
    // we never load more in pagination mode
    if (getPagingMode(state) === 'page') {
        return false;
    }
    var currentStream = getTopStream(state);
    if (!currentStream ||
        !isLoadableStream(currentStream) ||
        getIsLoading(state)) {
        return false;
    }
    if (currentStream.enableLoader) {
        var items = getStreamItems(state, currentStream);
        var currentIndex = getCurrentItemIndex(state);
        var lookAheadSteps = 2;
        switch (direction) {
            case 'next':
                return (streamSelectors.hasMore(state, currentStream.streamId) &&
                    currentIndex + lookAheadSteps >= items.length);
            case 'prev':
                return (streamSelectors.getHasLess(state, currentStream.streamId) &&
                    currentIndex - lookAheadSteps < 0);
            default:
                return false;
        }
    }
    return false;
}
export function getCurrentDuperbrowseContext(state, itemCount) {
    if (itemCount === void 0) { itemCount = 9; }
    var currentStream = getTopStream(state);
    if (!currentStream) {
        return;
    }
    var items = getStreamItems(state, currentStream);
    var currentIndex = getCurrentItemIndex(state);
    if (currentIndex < 0) {
        return;
    }
    var itemsToDuperbrowseItemList = function (array) {
        return array
            .map(function (item) { return getSimpleItem(state, item, currentStream.itemSchema); })
            .filter(function (item) { return !!item; });
    };
    if (items.length <= itemCount) {
        return itemsToDuperbrowseItemList(items);
    }
    // we want half the items before the current one, if possible
    var itemsToShowBefore = Math.floor((itemCount - 1) / 2);
    var startIndex = Math.max(currentIndex - itemsToShowBefore, 0);
    return itemsToDuperbrowseItemList(items.slice(startIndex, startIndex + itemCount));
}
export function getLoadCount(state) {
    var _a;
    return (_a = getDuperbrowseState(state).loadCount) !== null && _a !== void 0 ? _a : 0;
}
//# sourceMappingURL=selectors.js.map